import { useCallback, useState } from "react";
import { z } from "zod";
import { useScope } from "@state/scopeContext.lib";
import { useUser } from "./useUser";

const columnSizingSchema = z.record(z.string(), z.number());

function readFromLocalStorage(cacheKey: string): Record<string, number> {
    const raw = localStorage.getItem(cacheKey);
    if (!raw) {
        return {};
    }
    try {
        const result = columnSizingSchema.safeParse(JSON.parse(raw));
        return result.success ? result.data : {};
    } catch {
        return {};
    }
}

/**
 * Persists user-resized column widths for a specific task.
 *
 * Backed by localStorage (scoped to workspace + user + taskId) with a React
 * state layer so updates flow synchronously into the view without a remount.
 * Returns [columnSizes, setColumnSizes] where columnSizes is a map of
 * columnId → pixel width containing only the columns the user has resized.
 */
export function useTaskColumnSizingCache(
    taskId: string,
): [Record<string, number>, (sizes: Record<string, number>) => void] {
    const { activeWorkspaceKey } = useScope();
    const user = useUser();

    const cacheKey = `${activeWorkspaceKey}/task-column-sizing-${taskId}-${user.id}`;

    const [columnSizes, setColumnSizes] = useState<Record<string, number>>(() =>
        readFromLocalStorage(cacheKey),
    );

    const persistColumnSizes = useCallback(
        (sizes: Record<string, number>) => {
            localStorage.setItem(cacheKey, JSON.stringify(sizes));
            setColumnSizes(sizes);
        },
        [cacheKey, setColumnSizes],
    );

    return [columnSizes, persistColumnSizes];
}
